import { Capacitor } from '@capacitor/core';

export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

export function getPlatform(): string {
  return Capacitor.getPlatform();
}

const GENERIC_HTTP_MESSAGE = /^Request failed with status code \d+$/i;
const BARE_STATUS_CODE = /^\d{3}$/;
const WRAPPER_FIELD_KEYS = new Set(['body', 'query', 'params', 'headers', 'formErrors', 'fieldErrors']);

function isMachineMessage(message: string): boolean {
  const trimmed = message.trim();
  return !trimmed || GENERIC_HTTP_MESSAGE.test(trimmed) || BARE_STATUS_CODE.test(trimmed);
}

function humanizeFieldName(field: string): string {
  if (WRAPPER_FIELD_KEYS.has(field)) return '';
  return field
    .replace(/\[(\d+)\]/g, ' $1')
    .replace(/[._]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

function collectErrorStrings(value: unknown, field = '', into: string[] = []): string[] {
  if (value == null) return into;
  if (typeof value === 'string') {
    if (!isMachineMessage(value)) {
      const label = humanizeFieldName(field);
      into.push(label ? `${label}: ${value}` : value);
    }
    return into;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectErrorStrings(item, field, into));
    return into;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (typeof record.message === 'string' && (typeof record.field === 'string' || typeof record.path === 'string')) {
      const path = String(record.field ?? record.path);
      collectErrorStrings(record.message, path || field, into);
      return into;
    }
    if (Array.isArray(record.formErrors) || record.fieldErrors) {
      collectErrorStrings(record.formErrors, field, into);
      collectErrorStrings(record.fieldErrors, field, into);
      return into;
    }
    Object.entries(record).forEach(([key, nested]) => {
      collectErrorStrings(nested, key, into);
    });
  }
  return into;
}

function messageForStatus(status?: number, fallback = 'Something went wrong'): string {
  if (!status) return fallback;
  if (status === 400 || status === 422) return 'Please check the form and try again.';
  if (status === 401) return 'Your session expired. Please sign in again.';
  if (status === 403) return 'You do not have permission to do this.';
  if (status === 404) return 'The requested item was not found.';
  if (status === 409) return 'This action conflicts with existing data.';
  if (status === 429) return 'Too many requests. Please wait and try again.';
  if (status >= 500) return 'Server error. Please try again later.';
  return fallback;
}

export function getErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (!error) return fallback;
  if (typeof error === 'string') {
    return isMachineMessage(error) ? fallback : error;
  }

  const maybe = error as {
    response?: { status?: number; data?: unknown };
    data?: unknown;
    errors?: unknown;
    message?: string;
    status?: number;
  };

  const payload =
    (maybe.response?.data && typeof maybe.response.data === 'object' ? maybe.response.data : null) ||
    (maybe.data && typeof maybe.data === 'object' && ('message' in maybe.data || 'errors' in maybe.data)
      ? maybe.data
      : null) ||
    (typeof maybe.message === 'string' && maybe.errors ? maybe : null);

  if (payload && typeof payload === 'object') {
    const body = payload as { message?: unknown; errors?: unknown; error?: unknown };
    const fieldMessages = collectErrorStrings(body.errors);
    if (fieldMessages.length > 0) {
      return [...new Set(fieldMessages)].join(' ');
    }
    if (typeof body.message === 'string' && !isMachineMessage(body.message)) {
      return body.message;
    }
    if (body.error && typeof body.error === 'object') {
      const errObj = body.error as { message?: unknown };
      if (typeof errObj.message === 'string' && !isMachineMessage(errObj.message)) {
        return errObj.message;
      }
    } else if (typeof body.error === 'string' && !isMachineMessage(body.error)) {
      return body.error;
    }
  }

  if (typeof maybe.message === 'string' && !isMachineMessage(maybe.message)) {
    return maybe.message;
  }

  return messageForStatus(maybe.response?.status ?? maybe.status, fallback);
}

export function displayName(user?: {
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
} | null): string {
  if (!user) return 'User';
  if (user.name) return user.name;
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (full) return full;
  return user.email || 'User';
}

export const getUserDisplayName = displayName;

export function isMarketingUser(user?: {
  role?: string;
  roles?: string[];
  scope?: string;
} | null): boolean {
  if (!user) return false;
  return Boolean(
    user.role === 'MARKETING_PERSON' ||
    user.role === 'MARKETING_AGENT' ||
    user.roles?.includes('MARKETING_PERSON') ||
    user.roles?.includes('MARKETING_AGENT')
  );
}

export function homePathAfterLogin(user?: {
  role?: string;
  roles?: string[];
  scope?: string;
  license?: { locked?: boolean } | null;
} | null): string {
  if (isMarketingUser(user)) return '/marketing';

  const isSuperAdmin = Boolean(
    user?.roles?.includes('SUPER_ADMIN') ||
    user?.role === 'SUPER_ADMIN' ||
    (user?.scope === 'PLATFORM' && !isMarketingUser(user))
  );
  if (isSuperAdmin) return '/super-admin';

  const isWatchman = Boolean(user?.roles?.includes('WATCHMAN') || user?.role === 'WATCHMAN');
  if (isWatchman) return '/watchman';

  if (user?.license?.locked) return '/settings/license';
  return '/dashboard';
}

export function formatCurrency(amount: number, currency = 'INR'): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}
